// ────────────────────────────────────────────────────────────────────────────
// Events for the Global Filter MFE
//
// Follows the same convention as:
//   - src/features/classification/constants/events.ts
//   - src/features/publication-wall/constants/events.ts
// ────────────────────────────────────────────────────────────────────────────

export const GLOBAL_FILTER_EVENTS = {
  // ── MFE → Warroom (outbound) ──────────────────────────────────────────────

  /**
   * Fired when the global filter modal closes (after the closing animation).
   * detail: {}
   */
  CLOSED: 'globalFilterClosed',

  /**
   * Fired when the user clicks "Apply filter".
   * detail: { filterId: string, dateRange: string }
   * 
   * filterId: encoded filter ID returned by /filters/encode — required by the
   *           warroom to call /filters/decode and render filter bar labels.
   * dateRange: formatted date range from the period field (e.g., '7d', 'custom-range')
   * 
   * IMPORTANT: This event MUST contain ONLY these two fields. Do not add extra fields.
   */
  APPLIED: 'mfe-global-filter-applied',

  /**
   * Fired when the user clicks "Clear".
   * detail: {}
   */
  CLEARED: 'mfe-global-filter-cleared',

  /**
   * Fired when the user clicks "Zoom".
   * detail: {}
   */
  ZOOM_CLICKED: 'mfe-global-filter-zoom-clicked',

  /**
   * Fired when the user saves a named filter.
   * detail: { name: string }
   */
  SAVE_CLICKED: 'mfe-global-filter-save-clicked',

  /**
   * Fired when the user toggles the "Mar Aberto" or "Canais Proprietários" chips.
   * detail: { chip: 'openSea' | 'proprietaryChannels' }
   */
  CHIP_CHANGED: 'mfe-global-filter-chip-changed',

  /**
   * Fired when the user saves changes to an existing saved filter.
   * detail: { filterId: string }
   */
  EDIT_SAVE_CLICKED: 'mfe-global-filter-edit-save-clicked',

  /**
   * Fired when the user deletes a saved filter.
   * detail: { filterId: string }
   */
  DELETE_CLICKED: 'mfe-global-filter-delete-clicked',

  /**
   * Fired when the Conversations > Channels selection changes.
   * detail: { selection: SelectionMap }
   */
  CONVERSATION_CHANNELS_CHANGED: 'mfe-global-filter-conversation-channels-changed',

  /**
   * Fired when the Conversations > Pages selection changes.
   * detail: { selection: SelectionMap }
   */
  CONVERSATION_PAGES_CHANGED: 'mfe-global-filter-conversation-pages-changed',

  /**
   * Fired when the Conversations > Respondent selection changes.
   * detail: { selection: SelectionMap }
   */
  CONVERSATION_RESPONDENTS_CHANGED: 'mfe-global-filter-conversation-respondents-changed',

  /**
   * Fired when the Conversations > Routing selection changes.
   * detail: { selection: SelectionMap }
   */
  CONVERSATION_ROUTING_CHANGED: 'mfe-global-filter-conversation-routing-changed',

  /**
   * Fired to ask Warroom to provide conversation channels options.
   * detail: {}
   */
  REQUEST_CONVERSATION_CHANNELS_OPTIONS: 'mfe-global-filter-request-conversation-channels-options',

  /**
   * Fired to ask Warroom to provide conversation pages options.
   * detail: {}
   */
  REQUEST_CONVERSATION_PAGES_OPTIONS: 'mfe-global-filter-request-conversation-pages-options',

  /**
   * Fired to ask Warroom to provide conversation respondents options.
   * detail: {}
   */
  REQUEST_CONVERSATION_RESPONDENTS_OPTIONS: 'mfe-global-filter-request-conversation-respondents-options',

  /**
   * Fired to ask Warroom to provide conversation routing options.
   * detail: {}
   */
  REQUEST_CONVERSATION_ROUTING_OPTIONS: 'mfe-global-filter-request-conversation-routing-options',

  /**
   * Fired when conversation respondents options are loaded.
   * detail: { count: number }
   */
  CONVERSATION_RESPONDENTS_OPTIONS_LOADED: 'mfe-global-filter-conversation-respondents-options-loaded',

  /**
   * Fired when conversation routing options are loaded.
   * detail: { count: number }
   */
  CONVERSATION_ROUTING_OPTIONS_LOADED: 'mfe-global-filter-conversation-routing-options-loaded',

  /**
   * Warroom dispatches this to exclude certain channels from the filter.
   * detail: { channels: string[] }
   */
  CHANNELS_EXCLUDE: 'mfe-global-filter-channels-exclude',

  // ── Warroom → MFE (inbound) ──────────────────────────────────────────────
  /**
   * Warroom dispatches this to update channels options for Conversations section.
   * detail: { options: Array<string | { key: string, label: string, iconPlatform?: string }> }
   */
  UPDATE_CONVERSATION_CHANNELS_OPTIONS: 'mfe-global-filter-update-conversation-channels-options',

  /**
   * Warroom dispatches this to update pages options for Conversations section.
   * detail: { options: Array<...> }
   */
  UPDATE_CONVERSATION_PAGES_OPTIONS: 'mfe-global-filter-update-conversation-pages-options',

  /**
   * Warroom dispatches this to update respondents options for Conversations section.
   * detail: { options: Array<string | { key: string, label: string }> }
   */
  UPDATE_CONVERSATION_RESPONDENTS_OPTIONS: 'mfe-global-filter-update-conversation-respondents-options',

  /**
   * Warroom dispatches this to update routing options for Conversations section.
   * detail: { options: Array<string | { key: string, label: string }> }
   */
  UPDATE_CONVERSATION_ROUTING_OPTIONS: 'mfe-global-filter-update-conversation-routing-options',

  /**
   * Warroom dispatches this when zoom creation succeeds.
   * detail: {}
   */
  NOTIFY_ZOOM_SUCCESS: 'mfe-global-filter-zoom-success',

  /**
   * Warroom dispatches this when zoom creation fails.
   * detail: {}
   */
  NOTIFY_ZOOM_ERROR: 'mfe-global-filter-zoom-error',
} as const;

export type GlobalFilterEventName = (typeof GLOBAL_FILTER_EVENTS)[keyof typeof GLOBAL_FILTER_EVENTS];
